import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Clock, User, Briefcase, ChevronRight, CalendarDays, Printer, Wrench } from 'lucide-react';
import { prettyCategory } from './ShopTime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => {
  // Local date YYYY-MM-DD (avoids UTC off-by-one)
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const prettyDate = (iso) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

export default function DailySummary() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null); // Date object | null
  const [selectedTech, setSelectedTech] = useState(null); // tech object | null
  const [summary, setSummary] = useState(null);
  const [busyDates, setBusyDates] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);

  // Fetch busy-date markers for the current calendar month
  const fetchMonthMarkers = useCallback(async (monthDate) => {
    try {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth() + 1;
      const res = await axios.get(`${API}/reports/daily-summary-month`, {
        params: { year, month },
      });
      setBusyDates(res.data.busy_dates || []);
    } catch {
      setBusyDates([]);
    }
  }, []);

  useEffect(() => {
    fetchMonthMarkers(calendarMonth);
  }, [calendarMonth, fetchMonthMarkers]);

  const handleDayClick = async (date) => {
    if (!date) return;
    setSelectedDate(date);
    setSelectedTech(null);
    setSummary(null);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/daily-summary`, {
        params: { date: fmtDate(date) },
      });
      setSummary(res.data);
    } catch {
      toast.error('Failed to load daily summary');
    } finally {
      setLoading(false);
    }
  };

  const goBackToCalendar = () => {
    setSelectedDate(null);
    setSelectedTech(null);
    setSummary(null);
  };

  const goBackToDay = () => {
    setSelectedTech(null);
  };

  const shiftDay = (deltaDays) => {
    if (!selectedDate) return;
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + deltaDays);
    // Drop any tech drill-down when shifting — the new day has different data.
    setSelectedTech(null);
    // Keep the calendar view in sync so Back lands on the correct month.
    if (next.getMonth() !== calendarMonth.getMonth() || next.getFullYear() !== calendarMonth.getFullYear()) {
      setCalendarMonth(new Date(next.getFullYear(), next.getMonth(), 1));
    }
    handleDayClick(next);
  };

  // Build a Set of busy-date strings (YYYY-MM-DD) for fast lookup in calendar modifier
  const busySet = new Set(busyDates);
  const isBusyDay = (date) => busySet.has(fmtDate(date));

  return (
    <DashboardLayout>
      <div data-testid="daily-summary-page" className="space-y-6">
        {/* Breadcrumb header */}
        <div className="flex items-center gap-2 flex-wrap">
          {selectedDate && (
            <Button
              variant="outline"
              size="sm"
              onClick={selectedTech ? goBackToDay : goBackToCalendar}
              data-testid="daily-summary-back-btn"
            >
              <ArrowLeft size={14} className="mr-1" />
              Back
            </Button>
          )}
          <div>
            <h3 className="text-2xl font-bold text-slate-900">Daily Summary</h3>
            <p className="text-slate-600 text-sm">
              {!selectedDate && 'Pick a day to see who worked and on what.'}
              {selectedDate && !selectedTech && prettyDate(fmtDate(selectedDate))}
              {selectedDate && selectedTech && (
                <>
                  <span className="font-semibold">{selectedTech.tech_name}</span> ·{' '}
                  {prettyDate(fmtDate(selectedDate))}
                </>
              )}
            </p>
          </div>
          {selectedDate && (
            <div className="ml-auto flex items-center gap-2" data-testid="day-nav">
              <Button
                variant="outline"
                size="sm"
                onClick={() => shiftDay(-1)}
                data-testid="prev-day-btn"
                title="Previous day"
              >
                <ArrowLeft size={14} className="mr-1" />
                Previous day
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => shiftDay(1)}
                data-testid="next-day-btn"
                title="Next day"
              >
                Next day
                <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          )}
        </div>

        {/* View 1: Calendar */}
        {!selectedDate && (
          <Card className="p-6 bg-white border border-slate-200 shadow-sm">
            <div className="flex items-start gap-6 flex-wrap">
              <div data-testid="daily-summary-calendar">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDayClick}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  modifiers={{ busy: isBusyDay }}
                  modifiersClassNames={{
                    busy: 'relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-orange-500',
                  }}
                  className="rounded-md border border-slate-200"
                />
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="rounded-md bg-slate-50 border border-slate-200 p-4">
                  <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <CalendarDays size={16} /> Legend
                  </p>
                  <p className="text-xs text-slate-600 flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full bg-orange-500" />
                    Day has tech assignments
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Tip: navigate months with the arrows to load markers for that month.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* View 2: Day Summary (techs list) */}
        {selectedDate && !selectedTech && (
          <Card className="p-6 bg-white border border-slate-200 shadow-sm">
            {loading && <p className="text-sm text-slate-500" data-testid="day-loading">Loading…</p>}
            {!loading && summary && summary.techs.length === 0 && (
              <div className="text-center py-12" data-testid="day-empty">
                <p className="text-slate-500">No technician assignments on this day.</p>
              </div>
            )}
            {!loading && summary && summary.techs.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
                  <p className="text-sm text-slate-600">
                    <span className="font-bold text-slate-900">{summary.total_techs}</span>{' '}
                    {summary.total_techs === 1 ? 'tech' : 'techs'} ·{' '}
                    <span className="font-bold text-slate-900">{summary.total_hours} hrs</span> total
                    {summary.total_overtime > 0 && (
                      <span className="ml-2 text-amber-700 font-semibold" data-testid="day-total-overtime">
                        (+{summary.total_overtime} OT)
                      </span>
                    )}
                  </p>
                  <Button
                    onClick={() => window.print()}
                    variant="outline"
                    size="sm"
                    data-testid="print-all-techs-btn"
                  >
                    <Printer size={14} className="mr-1" />
                    Print All
                  </Button>
                </div>
                <div className="divide-y divide-slate-200" data-testid="day-tech-list">
                  {summary.techs.map((tech) => (
                    <button
                      key={tech.tech_id}
                      onClick={() => setSelectedTech(tech)}
                      className="w-full flex items-center justify-between px-2 py-3 hover:bg-slate-50 transition text-left rounded"
                      data-testid={`day-tech-${tech.tech_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center">
                          <User size={18} className="text-slate-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{tech.tech_name}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Briefcase size={12} />
                            {tech.job_count} {tech.job_count === 1 ? 'job' : 'jobs'}
                            {tech.shop_count > 0 && ` · ${tech.shop_count} shop`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-slate-900 flex items-center gap-1">
                          <Clock size={14} className="text-slate-500" />
                          {tech.total_hours} hrs
                          {tech.total_overtime > 0 && (
                            <span className="ml-1 text-amber-700 text-xs font-semibold">
                              (+{tech.total_overtime} OT)
                            </span>
                          )}
                        </span>
                        <ChevronRight size={18} className="text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>

                {/* Hidden printable block — all techs, one page per tech */}
                <div id="all-techs-printable" className="hidden print:block">
                  {summary.techs.map((tech, idx) => (
                    <div
                      key={tech.tech_id}
                      className={`tech-print-block ${idx < summary.techs.length - 1 ? 'tech-print-break' : ''}`}
                    >
                      <div className="mb-4 border-b border-slate-300 pb-3">
                        <p className="text-xs uppercase tracking-wider text-slate-500">
                          ShopManager247 — Daily Summary
                        </p>
                        <p className="text-sm text-slate-700">{prettyDate(fmtDate(selectedDate))}</p>
                      </div>
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
                        <div>
                          <p className="font-bold text-slate-900 text-lg">{tech.tech_name}</p>
                          <p className="text-xs text-slate-500">
                            {tech.job_count} {tech.job_count === 1 ? 'job' : 'jobs'}
                            {tech.shop_count > 0 && ` · ${tech.shop_count} shop`} this day
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-slate-900">
                            {tech.total_hours} hrs
                          </p>
                          {tech.total_overtime > 0 && (
                            <p className="text-sm font-semibold text-amber-700">
                              +{tech.total_overtime} OT hrs
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {tech.jobs.map((row, ri) => {
                          const isShop = row.kind === 'shop';
                          return (
                            <div
                              key={`${tech.tech_id}-${ri}`}
                              className="flex items-center justify-between px-4 py-3 border border-slate-200 rounded-md"
                            >
                              <div>
                                {isShop ? (
                                  <>
                                    <p className="font-semibold text-slate-900">
                                      Shop · {prettyCategory(row.category)}
                                      {row.equipment_name && (
                                        <span className="text-slate-500 font-normal"> · {row.equipment_name}</span>
                                      )}
                                    </p>
                                    {row.description && (
                                      <p className="text-xs text-slate-500">{row.description}</p>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <p className="font-semibold text-slate-900">
                                      Job #{row.job_number}
                                      {row.customer_name && (
                                        <span className="text-slate-500 font-normal"> · {row.customer_name}</span>
                                      )}
                                    </p>
                                    {(row.make || row.model) && (
                                      <p className="text-xs text-slate-500">
                                        {[row.make, row.model].filter(Boolean).join(' ')}
                                        {row.status && (
                                          <span className="ml-2 uppercase tracking-wider">· {row.status}</span>
                                        )}
                                      </p>
                                    )}
                                  </>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-bold text-slate-900">{row.hours} hrs</p>
                                {row.overtime_hours > 0 && (
                                  <p className="text-xs font-semibold text-amber-700">
                                    +{row.overtime_hours} OT
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Supervisor signature line */}
                      <div className="mt-10 pt-4 grid grid-cols-2 gap-8">
                        <div>
                          <div className="border-b border-slate-700 h-8" />
                          <p className="text-xs text-slate-600 mt-1">Supervisor signature</p>
                        </div>
                        <div>
                          <div className="border-b border-slate-700 h-8" />
                          <p className="text-xs text-slate-600 mt-1">Date</p>
                        </div>
                      </div>
                      <div className="mt-6 pt-3 border-t border-slate-300 text-xs text-slate-500">
                        <p>Printed {new Date().toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        )}

        {/* View 3: Tech Day Breakdown */}
        {selectedDate && selectedTech && (
          <div id="tech-day-printable">
            <div className="flex items-center justify-end mb-3 no-print">
              <Button
                onClick={() => window.print()}
                variant="outline"
                size="sm"
                data-testid="print-tech-day-btn"
              >
                <Printer size={14} className="mr-1" />
                Print
              </Button>
            </div>
            <Card className="p-6 bg-white border border-slate-200 shadow-sm">
              {/* Print-only header (org/branding-style header for the printable copy) */}
              <div className="hidden print:block mb-4 border-b border-slate-300 pb-3">
                <p className="text-xs uppercase tracking-wider text-slate-500">ShopManager247 — Daily Summary</p>
                <p className="text-sm text-slate-700">{prettyDate(fmtDate(selectedDate))}</p>
              </div>
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                    <User size={20} className="text-slate-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{selectedTech.tech_name}</p>
                    <p className="text-xs text-slate-500">
                      {selectedTech.job_count} {selectedTech.job_count === 1 ? 'job' : 'jobs'}
                      {selectedTech.shop_count > 0 && ` · ${selectedTech.shop_count} shop`} this day
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-slate-900 flex items-center justify-end gap-1">
                    <Clock size={18} className="text-slate-500" />
                    {selectedTech.total_hours} hrs
                  </p>
                  {selectedTech.total_overtime > 0 && (
                    <p className="text-sm font-semibold text-amber-700" data-testid="tech-total-overtime">
                      +{selectedTech.total_overtime} OT hrs
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-2" data-testid="tech-job-breakdown">
                {selectedTech.jobs.map((row) => {
                  const isShop = row.kind === 'shop';
                  const key = isShop
                    ? `shop-${row.category}-${row.equipment_id || 'none'}`
                    : `job-${row.job_id}`;
                  const onClick = isShop ? undefined : () => navigate(`/jobs/${row.job_id}`);
                  return (
                    <button
                      key={key}
                      onClick={onClick}
                      disabled={isShop}
                      className={`w-full flex items-center justify-between px-4 py-3 border rounded-md text-left ${
                        isShop
                          ? 'border-amber-200 bg-amber-50/40 cursor-default'
                          : 'border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition'
                      }`}
                      data-testid={isShop ? `tech-shop-${row.category}` : `tech-job-${row.job_id}`}
                    >
                      <div className="flex items-start gap-2">
                        {isShop ? (
                          <Wrench size={16} className="text-amber-700 mt-0.5" />
                        ) : (
                          <Briefcase size={16} className="text-slate-500 mt-0.5" />
                        )}
                        <div>
                          {isShop ? (
                            <>
                              <p className="font-semibold text-slate-900">
                                Shop · {prettyCategory(row.category)}
                                {row.equipment_name && (
                                  <span className="text-slate-500 font-normal"> · {row.equipment_name}</span>
                                )}
                              </p>
                              {row.description && (
                                <p className="text-xs text-slate-500">{row.description}</p>
                              )}
                            </>
                          ) : (
                            <>
                              <p className="font-semibold text-slate-900">
                                Job #{row.job_number}
                                {row.customer_name && (
                                  <span className="text-slate-500 font-normal"> · {row.customer_name}</span>
                                )}
                              </p>
                              {(row.make || row.model) && (
                                <p className="text-xs text-slate-500">
                                  {[row.make, row.model].filter(Boolean).join(' ')}
                                  {row.status && <span className="ml-2 uppercase tracking-wider">· {row.status}</span>}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className="text-sm font-bold text-slate-900 flex items-center gap-1">
                            <Clock size={14} className="text-slate-500" />
                            {row.hours} hrs
                          </span>
                          {row.overtime_hours > 0 && (
                            <p className="text-xs font-semibold text-amber-700">
                              +{row.overtime_hours} OT
                            </p>
                          )}
                        </div>
                        {!isShop && <ChevronRight size={16} className="text-slate-400 no-print" />}
                      </div>
                    </button>
                  );
                })}
              </div>
              {/* Supervisor signature line (print only) */}
              <div className="hidden print:grid mt-10 pt-4 grid-cols-2 gap-8">
                <div>
                  <div className="border-b border-slate-700 h-8" />
                  <p className="text-xs text-slate-600 mt-1">Supervisor signature</p>
                </div>
                <div>
                  <div className="border-b border-slate-700 h-8" />
                  <p className="text-xs text-slate-600 mt-1">Date</p>
                </div>
              </div>
              {/* Print-only footer */}
              <div className="hidden print:block mt-8 pt-4 border-t border-slate-300 text-xs text-slate-500">
                <p>Printed {new Date().toLocaleString()}</p>
              </div>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
